import pluginService from "api/pluginService";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { blobToBase64 } from "utils/blobToBase64";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import editorService from "api/editorService";
import { useContext, useMemo, useState } from "react";
import CertContext from "contexts/CertContext";
import signatureService from "api/signatureService";
import { useNavigate } from "react-router-dom";
import Button from "components/Button";

import { ItemKind } from "dto/IKindItems";
import dossierService from "api/dossierService";
import ChooseSigningCertificate from "components/ChooseSigningCertificate";
import listService from "api/listService";
import { IKey } from "dto/IKey";
import * as openpgp from "openpgp";
import { handleCreate } from "utils/uploadFile";
import DossierFilesContext from "contexts/DossierFilesContext";
import { useTranslation } from "react-i18next";
import generateLettersService from "api/generateLettersService";
import { ENotificationType, notification } from "utils/notification";

/**
 * Po podpisaniu pisma (EditorDocument) sprawdza, czy istnieje oczekująca generacja
 * pism wychodzących („przekaż do podpisu"). Jeśli tak — domyka ją: przesyłki są
 * generowane na koncie autora i trafiają do biura podawczego. Brak oczekującej
 * generacji (404) = zwykły podpis dokumentu → nic nie robimy.
 */
const tryCompleteLetterBatch = async (documentId: string) => {
  try {
    const pending = await generateLettersService.getPendingBatch(documentId);
    if (pending?.data?.Generation?.DocumentId) {
      await generateLettersService.completeSign({
        Generation: pending.data.Generation,
      });
      notification({
        type: ENotificationType.SUCCESS,
        title: "Wygenerowano pisma",
        text: "Podpisane pisma trafiły do biura podawczego (do wysyłki).",
      });
    }
  } catch {
    // Brak oczekującej generacji — zwykły podpis dokumentu.
  }
};

const CertificateApi: FC<Props> = ({
  itemId,
  itemKind,
  fileName,
  attachmentUri,
  file,
  originalFile,
  signatureField,
  onSigned,
}) => {
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");

  const navigate = useNavigate();

  const { dossierFiles, setDossierFiles } = useContext(DossierFilesContext);

  const { data: certsData } = pluginService.useGetCerificates();
  const { mutate: signDocument } =
    pluginService.useSignDocument();
  const { mutate: editorSign } = editorService.useSign();
  const { mutate: dossierSign } = dossierService.useSign();
  const { mutate: verify } = pluginService.useVerifySignature();
  const { mutate: uploadCert } = signatureService.useUploadSignature();

  const { data: packageData } = listService.useGetPackage({
    FilterId: itemId,
    Kind: itemKind,
    enabled:
      itemKind !== ItemKind.CaseFile && itemKind !== ItemKind.EditorDocument,
  });

  const { data: dossierData } = dossierService.useGetItems({
    Id: itemId,
    Kind: itemKind,
    PaginationModel: {
      page: 0,
      pageSize: 20,
    },
    enabled:
      itemKind === ItemKind.CaseFile || itemKind === ItemKind.EditorDocument,
  });

  const key: IKey = useMemo(
    () =>
      dossierData?.data.ObjectKeys?.[0] ?? packageData?.data.ObjectKeys?.[0],
    [dossierData, packageData]
  );

  const { cert: certContext, setCert } = useContext(CertContext);

  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const [showModal, setShowModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const verifyCert = (
    content: string,
    contentType: string,
    fileName: string,
    attachmentId: string,
    xades?: string,
    signedPdfUri?: string
  ) => {
    verify(
      {
        FileName: fileName,
        Content: content,
        ContentType: contentType,
        Xades: xades,
      },
      {
        onError: () => {
          setIsLoading(false);
        },
        onSuccess: (data: any) => {
          uploadCert(
            {
              Json: JSON.stringify(data.data),
              OwnerAttachmentId: attachmentId,
              OwnerId: certContext.id,
              OwnerKind: itemKind,
            },
            {
              onSuccess: () => {
                setCert({
                  id: "",
                  name: "",
                });
                setIsLoading(false);

                if (dossierData?.data) {
                  const files = [...dossierFiles];

                  const el = files.find(
                    (c) => c.Id === itemId || c.DossierId === itemId
                  );

                  if (el) {
                    el.SignedPdfUri = signedPdfUri;
                  }

                  setDossierFiles(files);
                }
                const newUrl = `${window.location.pathname}?pdf=${PDFId.replace(
                  attachmentUri,
                  attachmentId
                )}&signed=true`;
                navigate(newUrl);
              },
            }
          );
        },
      }
    );
  };

  const handleSign = async (cert: any) => {
    setShowModal(false);
    setIsLoading(true);

    if (!key) {
      console.error("No encryption key found. Can't continue");
      return;
    }

    const objectPublicKeyData = await readFileFromBase64(
      key.PublicKey,
      "application/other"
    );

    const objectPublicKey = await openpgp.key.read(
      new Uint8Array(objectPublicKeyData)
    );

    const b: string =
      itemKind === ItemKind.EditorDocument || itemKind === ItemKind.CaseFile
        ? ((await blobToBase64(file as Blob)) as string)
        : ((await blobToBase64(originalFile as Blob)) as string);

    signDocument(
      {
        CertificateSerialNumber: cert.SerialNumber,
        Content: b.split(",")[1],
        ContentType:
          itemKind === ItemKind.EditorDocument || itemKind === ItemKind.CaseFile
            ? "application/pdf"
            : "",
        FileName:
          itemKind === ItemKind.EditorDocument || itemKind === ItemKind.CaseFile
            ? `${certContext.name}.pdf`
            : fileName,
        GenerateReport: true,
        FieldAppearance: signatureField
          ? {
              Height: signatureField.height,
              Width: signatureField.width,
              Page: signatureField.page > 0 ? signatureField.page : 1,
              PageHeight: signatureField.pheight,
              PageWidth: signatureField.pwidth,
              X: signatureField.x,
              Y: signatureField.y,
            }
          : null,
      },
      {
        onError: () => {
          setIsLoading(false);
        },
        onSuccess: async (data: any) => {
          const fileName = data.data.FileName;
          const fileType = data.data.ContentType;
          const fileContent = data.data.Content;
          const xades = data.data.IsXades;
          const report = data.data.Report;

          const buffer = await readFileFromBase64(
            fileContent,
            xades ? "text/xml" : "application/pdf"
          );
          const blob = new Blob([buffer]);

          const uploadResult = await handleCreate(
            [blob],
            "",
            objectPublicKey,
            true,
            true,
            fileName,
            fileType,
            (fileIndex, progress) => {},  // eslint-disable-line @typescript-eslint/no-unused-vars
            false
          );

          if (!uploadResult) {
            return;
          }
          if (itemKind === ItemKind.EditorDocument) {
            editorSign(
              {
                Id: certContext.id,
                Uri: uploadResult[0].uri,
                Report: report ? JSON.stringify(report) : undefined,
                Countersign: false,
              },
              {
                onSuccess: () => {
                  if (!report) {
                    verifyCert(
                      fileContent,
                      "application/pdf",
                      fileName,
                      uploadResult[0].uri,
                      xades ? null : uploadResult[0].uri
                    );
                    onSigned?.(blob);
                  } else {
                    onSigned?.(blob);
                  }
                  // Domknięcie generacji pism wychodzących, jeśli pismo było
                  // przekazane do podpisu (obieg biura podawczego).
                  void tryCompleteLetterBatch(certContext.id);
                },
                onError: () => {
                  setIsLoading(false);
                },
              }
            );
          } else if (itemKind === ItemKind.CaseFile) {
            dossierSign(
              {
                Id: certContext.id,
                Uri: xades ? null : uploadResult[0].uri,
                XadesUri: xades ? uploadResult[0].uri : null,
                Report: report ? JSON.stringify(report) : undefined,
                Countersign: false,
              },
              {
                onSuccess: () => {
                  setIsLoading(false);

                  if (!report) {
                    verifyCert(
                      b.split(",")[1],
                      "application/pdf",
                      fileName,
                      uploadResult[0].uri,
                      fileContent,
                      xades ? null : uploadResult[0].uri
                    );
                    onSigned?.(blob);
                  } else {
                    onSigned?.(blob);
                    // if (dossierData?.data) {
                    //   let files = [...dossierFiles];

                    //   let el = files.find(
                    //     (c) => c.Id === itemId || c.DossierId === itemId
                    //   );

                    //   if (el) {
                    //     el.SignedPdfUri = xades ? null : uploadResult[0].uri;
                    //     el.SignedXadesUri = xades ? uploadResult[0].uri : null;
                    //   }

                    //   setDossierFiles(files);
                    // }
                    setCert(null);
                    //navigate(newUrl);
                  }
                },
                onError: () => {
                  setIsLoading(false);
                },
              }
            );
          }
        },
      }
    );
  };

  // return (
  //   <>
  //     {showModal && <ChooseCert onClick={handleSign} certs={certs} />}
  //     <div className={styles.wrapper}>
  //       <div className={styles.wrap}>
  //         Zweryfikuj treść pisma, a następnie{' '}
  //         <Button
  //           loading={isSignLoading}
  //           className={styles.button}
  //           text="Podpisz elektronicznie"
  //           onClick={() => setShowModal(true)}
  //         />
  //       </div>
  //     </div>
  //   </>
  // );

  return (
    <div className={styles.wrapper}>
      <div className={styles.left}>
        {t("certificate.verifyAndSign")}
        <div className={styles.hint}>
          {t("certificate.signHint")}
        </div>
      </div>
      <div className={styles.right}>
        <div
          onClick={() => {
            setCert({
              id: "",
              name: "",
            });
          }}
          className={styles.cta}
        >
          {t("common.cancel")}
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            className={styles.button}
            onClick={() => setShowModal(true)}
            text={t("certificate.signElectronically")}
            loading={isLoading}
          />
          {showModal && (
            <ChooseSigningCertificate
              toggle={() => setShowModal(false)}
              onClick={handleSign}
              certs={certs}
              allowChooseMethod={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateApi;
